import FeatureApi from '@/api/Feature'
import type {
  FeatureFetchData,
  FeatureFetchParams,
} from '@/api/Feature/types.ts'
import type { Paginated, ParamType } from '@/api/types.ts'
import {
  queryOptions,
  useMutation,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'

export function getFeatureQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return queryOptions({
    queryKey: ['get-initiative-feature', initiativeId, featureId],
    queryFn: () => FeatureApi.get([initiativeId, featureId]),
  })
}

export function useGetFeature(initiativeId: ParamType, featureId: ParamType) {
  return useQuery(getFeatureQueryOptions(initiativeId, featureId))
}

export function useFetchFeature(
  initiativeId: ParamType,
  params: FeatureFetchParams = {},
  options?: Omit<
    UseQueryOptions<unknown, unknown, Paginated<FeatureFetchData>, unknown[]>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    ...options,
    queryKey: ['fetch-feature', initiativeId, params],
    queryFn: () => FeatureApi.fetch([initiativeId], params),
  })
}

export function getChildFeaturesQueryOptions(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return queryOptions({
    queryKey: ['get-child-initiatives', initiativeId, featureId],
    queryFn: () => FeatureApi.fetchChildren([initiativeId, featureId]),
  })
}

export function useGetChildFeatures(
  initiativeId: ParamType,
  featureId: ParamType
) {
  return useQuery(getChildFeaturesQueryOptions(initiativeId, featureId))
}

export function useGetFeatureByLink() {
  return useMutation({
    mutationFn: (key: string) => FeatureApi.fetchByLink(key),
  })
}

export function useFetchLinkedCollects(
  initiativeId: ParamType,
  featureId: ParamType,
  collectId: ParamType
) {
  return useQuery({
    queryKey: ['fetch-linked-collects', initiativeId, featureId, collectId],
    queryFn: () =>
      FeatureApi.fetchLinkedCollects([initiativeId, featureId, collectId]),
  })
}
